import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import { usePageFrame } from '@/lib/frame'

type Invite = { email: string; created_at: number }

// The owner's list of who may create an account while gatheredfrom is invite-only.
export function Invites() {
  const queryClient = useQueryClient()
  usePageFrame(undefined, { title: 'Invites', back: { to: '/', label: 'Back' } })
  const [email, setEmail] = useState('')
  const invites = useQuery({ queryKey: ['invites'], queryFn: () => api<{ invites: Invite[] }>('/admin/invites') })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['invites'] })
  const add = useMutation({
    mutationFn: (email: string) => api('/admin/invites', { method: 'POST', body: { email } }),
    onSuccess: () => {
      setEmail('')
      return refresh()
    },
  })
  const remove = useMutation({
    mutationFn: (email: string) => api(`/admin/invites/${encodeURIComponent(email)}`, { method: 'DELETE' }),
    onSuccess: refresh,
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    add.mutate(email.trim())
  }

  return (
    <>
      <header className="flex items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <p className="eyebrow">Who can sign up</p>
          <h1 className="text-display">Invites</h1>
        </div>
        <Button variant="ghost" size="sm" className="max-nav:hidden" asChild>
          <Link to="/">Done</Link>
        </Button>
      </header>
      <form onSubmit={submit} className="grid gap-2">
        <Label htmlFor="invite-email">Invite someone by email</Label>
        <div className="flex gap-2">
          <Input id="invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button type="submit" disabled={add.isPending}>
            Invite
          </Button>
        </div>
        {add.error && (
          <Alert variant="destructive">
            <AlertDescription>{add.error.message}</AlertDescription>
          </Alert>
        )}
      </form>
      <ul className="divide-y rounded-md border">
        {invites.data?.invites.map((i) => (
          <li key={i.email} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
            <span className="truncate">{i.email}</span>
            <Button variant="ghost" size="sm" onClick={() => remove.mutate(i.email)}>
              Remove
            </Button>
          </li>
        ))}
        {invites.data?.invites.length === 0 && (
          <li className="px-3 py-2 text-sm text-muted-foreground">No one is invited yet.</li>
        )}
      </ul>
    </>
  )
}
